<template>
    <PageTemplateContent>
        <BaseHero
            title="Here's how to make a proper video."
            description="You don't have to be a professional content creator. Here are some tips, tricks and requirements to help you create a qualified video."
        />
        <!-- <BentoFrame
            data-span-all
            dark
        >
            <template #image>
                <GraphicDevice
                    :shape="3"
                    data-span-5
                >
                    <NuxtPicture
                        src="/stock/doctor-3.png"
                        alt=""
                        sizes="xs:100vw, md:40vw"
                        width="300px"
                        height="300px"
                        loading="lazy"
                        decoding="async"
                        style="--object-position: top;"
                        :img-attrs="{
                            fetchpriority: 'low'
                        }"
                    />
                </GraphicDevice>
            </template>
            <h2>How to make a good instruction video.</h2>
            <p>You don’t need to be a professional. Here are some requirements and guidelines to help guide you.</p>
        </BentoFrame> -->

        <BaseSection
            data-width="content"
            data-span-all
        >
            <BentoFrame
                data-span-3
                title="Requirements"
            >
                <div>
                    <h2>To meet the requirements for the peer-review process, we ask you to adhere to the following guidelines:</h2>
                    <DetailsList
                        :items="[
                            {
                                icon: 'thumbs-up',
                                title: 'Consent',
                                description: 'Get consent from the patient.'
                            },
                            {
                                icon: 'landscape',
                                title: 'Aspect ratio (16:9)',
                                description: 'Videos should be oriented horizontally'
                            },
                            {
                                icon: 'disabled-visible',
                                title: 'Patient privacy',
                                description: 'Sharing identifiable patient information is prohibited'
                            },
                            {
                                icon: 'camera',
                                title: 'Recommended video length',
                                description: 'Maximum of 10 minutes'
                            },
                        ]"
                    />
                </div>
            </BentoFrame>
        </BaseSection>

        <ContentBlocksText>
            <h2>Tips & tricks</h2>
            <p>Here are some practical guidelines to help you construct a high-quality video:</p>

            <h3>Address healthcare professionals</h3>
            <p>Your video content will be viewed by verified and licensed healthcare professionals, use professional terminology.</p>

            <h3>Be clear and concise</h3>
            <p>The attention span of your viewers varies. The recommended video length is 10 minutes.</p>

            <h3>Check the sound quality</h3>
            <p>Make sure your voice is appropriately loud and clear. Please avoid background noise.</p>

            <h3>Include a brief introduction</h3>
            <p>Start your video by introducing yourself and your affiliation, briefly explain the procedure and inform viewers about the specific devices you use.</p>
        </ContentBlocksText>

        <ContentBlocksText>
            <h2>Informed consent</h2>
            <p>Following European requirements, it is mandatory to get consent from the patient. One of the following two options will be sufficient;</p>
            <ol>
                <li>Download the Medpeers informed consent form and let the patient sign.</li>
                <li>Include a formal notification in the electronic patient file that the patient agreed with recording video content for educational purposes on MedPeers.</li>
            </ol>
            <p>Please verify which option aligns with protocols in your institute.</p>
            <BaseButton
                element="a"
                href="/pdf/medpeers_patient_consent_form.pdf"
                download
                class="base-button--outline"
            >
                Download consent form
            </BaseButton>
        </ContentBlocksText>

        <!-- <BaseSection
            data-span-all
            section-width="content"
            centered-title
        >
            <template #title>Instructional video</template>
            <template #subtitle>Take a look, we’re here to help you</template>
            <BentoFrame
                data-span-4
                dark
            >
                <iframe
                    src="https://iframe.mediadelivery.net/embed/175166/a87c9c71-d47c-4301-a6c5-7e50219779a4?autoplay=false&loop=false&muted=false&preload=true"
                    loading="lazy"
                    allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                    allowfullscreen="true"
                    style="border: 0; aspect-ratio: 16/9; width: 100%;"
                />
            </BentoFrame>
        </BaseSection> -->

        <BentoGrid class="bento-grid--bleed">
            <BentoFrame data-span-all>
                <h2>Submit your video content and share your expertise with peers.</h2>
                <BaseButton
                    v-if="authStore.loggedIn"
                    :element="defineNuxtLink({})"
                    to="/upload/checklist"
                >
                    Submit your video
                </BaseButton>
                <BaseButton
                    v-else
                    @click="modalStore.toggleModal(MODAL_REGISTER);"
                >
                    Create account
                </BaseButton>
            </BentoFrame>

            <BentoFrame
                data-span-all
                dark
            >
                <h2>Leave a message if you have any questions or want to get in touch with the MedPeers team.</h2>
                <p>We usually respond within 3 days.</p>

                <BaseButton
                    element="a"
                    href="mailto:support@medpeers.org"
                >
                    Contact us
                </BaseButton>
            </BentoFrame>
        </BentoGrid>
    </PageTemplateContent>
</template>

<script setup>
import { useAuthStore } from '~/store/auth';
import { useModalStore, MODAL_REGISTER } from '~/store/modal';

const { asset } = useBaseUrl();
const authStore = useAuthStore();
const modalStore = useModalStore();

useSeoMeta({
    title: 'MedPeers | How to make a video',
    description: 'A secure, peer-reviewed and quality-controlled platform offering educational videos of medical procedures',
    ogImage: asset('/images/og/default.png')
});
</script>
